/* PatientNotesSection — patient-scoped clinical notes timeline.
 *
 * Notes are attached to the patient and may optionally tag a case. Hybrid
 * editor: free-form or SOAP. Chronological list newest first; doctor's own
 * notes are editable / soft-deletable in place.
 *
 * Props:
 *   patientId, patientName, cases — patient context.
 *   currentUser — for ownership / edit-delete rights.
 *   caseFilter — when provided, the list shows notes tagged to this case
 *                only and the composer pre-tags new notes to it. Used by
 *                the case-page Notes tab so doctors see the same UI on
 *                the patient profile and on individual cases.
 */
import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { confirmDialog, alertDialog, promptDialog } from "@/lib/confirm";
import {
    Note, PencilSimple, TrashSimple, Stethoscope, Plus, X as CloseIcon,
} from "@phosphor-icons/react";

const SOAP_FIELDS = [
    { key: "subjective", label: "Subjective", hint: "What the patient reports" },
    { key: "objective",  label: "Objective",  hint: "Clinical findings" },
    { key: "assessment", label: "Assessment", hint: "Diagnosis / interpretation" },
    { key: "plan",       label: "Plan",       hint: "Next steps" },
];

const SOAP_TEMPLATE = `S — Subjective:
\nO — Objective:
\nA — Assessment:
\nP — Plan:
`;

export default function PatientNotesSection({ patientId, patientName, cases = [], currentUser, caseFilter = null }) {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [composerOpen, setComposerOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const load = async () => {
        if (!patientId) return;
        setLoading(true);
        try {
            const r = await api.get("/doctor/notes", { params: { patient_id: patientId } });
            const all = r.data || [];
            // When the host is a single case page, filter to that case's notes.
            // We still load all notes for the patient (cheap) so the patient
            // profile and the case page share the same cache shape.
            setNotes(caseFilter ? all.filter((n) => n.case_id === caseFilter) : all);
        } catch {
            // ignore
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [patientId, caseFilter]);

    const onSaved = () => { setComposerOpen(false); setEditingId(null); load(); };
    const editingNote = notes.find((n) => n.id === editingId);

    /* Case-code lookup for note rows that tagged a case. */
    const caseCodeById = useMemo(() => {
        const m = {};
        for (const c of cases) m[c.id] = c.case_code || c.code || c.id.slice(0, 6);
        return m;
    }, [cases]);

    const subtitle = caseFilter
        ? `Visit-by-visit record for this case · ${notes.length} note${notes.length === 1 ? "" : "s"}`
        : `Visit-by-visit record for ${patientName || "this patient"} · ${notes.length} note${notes.length === 1 ? "" : "s"}`;

    return (
        <div data-testid="patient-notes-section" className="bg-card border border-border rounded-2xl bento-shadow overflow-hidden">
            <div className="px-6 pt-5 pb-4 flex items-start justify-between gap-3 border-b border-border/60">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <Note size={14} weight="duotone" className="text-primary" />
                        <h3 className="font-display text-lg font-semibold tracking-tight">Clinical notes</h3>
                    </div>
                    <p className="text-xs text-muted-foreground">{subtitle}</p>
                </div>
                {!composerOpen && !editingId && (
                    <Button size="sm" onClick={() => setComposerOpen(true)} className="rounded-full" data-testid="patient-notes-add">
                        <Plus size={13} weight="bold" className="mr-1" /> Add note
                    </Button>
                )}
            </div>

            {composerOpen && (
                <NoteComposer
                    patientId={patientId}
                    cases={cases}
                    initialCaseId={caseFilter}
                    lockCase={!!caseFilter}
                    onCancel={() => setComposerOpen(false)}
                    onSaved={onSaved}
                />
            )}
            {editingNote && (
                <NoteComposer
                    patientId={patientId}
                    cases={cases}
                    existing={editingNote}
                    lockCase={!!caseFilter}
                    onCancel={() => setEditingId(null)}
                    onSaved={onSaved}
                />
            )}

            <div className="divide-y divide-border/40">
                {loading ? (
                    <div className="p-8 text-center text-xs text-muted-foreground">Loading…</div>
                ) : notes.length === 0 && !composerOpen ? (
                    <div className="p-10 text-center" data-testid="patient-notes-empty">
                        <div className="w-12 h-12 rounded-full bg-secondary mx-auto mb-3 flex items-center justify-center">
                            <Stethoscope size={20} weight="duotone" className="text-muted-foreground" />
                        </div>
                        <p className="text-sm text-foreground font-medium">
                            {caseFilter ? "No notes for this case yet" : "No notes for this patient yet"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 mb-4">Capture observations, treatment decisions, or what the patient reports.</p>
                        <Button size="sm" variant="outline" onClick={() => setComposerOpen(true)} className="rounded-full" data-testid="patient-notes-add-first">
                            Add first note
                        </Button>
                    </div>
                ) : (
                    notes.map((note) => (
                        <NoteRow
                            key={note.id}
                            note={note}
                            caseCode={note.case_id ? caseCodeById[note.case_id] : null}
                            currentUserId={currentUser?.id}
                            onEdit={() => setEditingId(note.id)}
                            onDeleted={load}
                            isEditing={note.id === editingId}
                            hideCaseChip={!!caseFilter}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

function NoteComposer({ patientId, cases, existing, onCancel, onSaved, initialCaseId = null, lockCase = false }) {
    const isEdit = !!existing;
    const [mode, setMode] = useState(existing?.note_type || "free");
    const [body, setBody] = useState(existing?.body || "");
    const [soap, setSoap] = useState(() => ({
        subjective: existing?.soap?.subjective || "",
        objective:  existing?.soap?.objective  || "",
        assessment: existing?.soap?.assessment || "",
        plan:       existing?.soap?.plan       || "",
    }));
    const [caseId, setCaseId] = useState(existing?.case_id || initialCaseId || "");
    const [visitAt, setVisitAt] = useState(
        existing?.visit_at ? existing.visit_at.slice(0, 10) : new Date().toISOString().slice(0, 10)
    );
    const [submitting, setSubmitting] = useState(false);

    const hasContent = mode === "free"
        ? !!body.trim()
        : Object.values(soap).some((v) => v.trim());

    const insertSoapTemplate = () => {
        setBody((prev) => prev.trim() ? `${prev}\n\n${SOAP_TEMPLATE}` : SOAP_TEMPLATE);
    };

    const save = async () => {
        if (!hasContent) return;
        setSubmitting(true);
        try {
            const payload = {
                patient_id: patientId,
                case_id: caseId || null,
                note_type: mode,
                body: mode === "free" ? body : "",
                soap: mode === "soap" ? soap : null,
                visit_at: new Date(visitAt).toISOString(),
            };
            if (isEdit) {
                await api.put(`/doctor/notes/${existing.id}`, payload);
                toast.success("Note updated");
            } else {
                await api.post("/doctor/notes", payload);
                toast.success("Note saved");
            }
            onSaved && onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save note");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="px-6 py-5 bg-secondary/20 border-b border-border/40" data-testid="note-composer">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-1 p-0.5 rounded-full bg-card border border-border">
                    <ModePill active={mode === "free"} onClick={() => setMode("free")} testid="note-mode-free">Free-form</ModePill>
                    <ModePill active={mode === "soap"} onClick={() => setMode("soap")} testid="note-mode-soap">SOAP</ModePill>
                </div>
                <div className="flex items-center gap-3">
                    {cases.length > 0 && !lockCase && (
                        <div className="flex items-center gap-2">
                            <Label htmlFor="note-case" className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Case</Label>
                            <select
                                id="note-case"
                                value={caseId}
                                onChange={(e) => setCaseId(e.target.value)}
                                className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                                data-testid="note-case-link"
                            >
                                <option value="">No case</option>
                                {cases.map((c) => (
                                    <option key={c.id} value={c.id}>{c.case_code || c.code || c.id.slice(0, 8)}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <Label htmlFor="visit-at" className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Visit date</Label>
                        <input
                            id="visit-at"
                            type="date"
                            value={visitAt}
                            onChange={(e) => setVisitAt(e.target.value)}
                            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                            data-testid="note-visit-at"
                        />
                    </div>
                </div>
            </div>

            {mode === "free" ? (
                <div className="space-y-2">
                    <Textarea
                        rows={6}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder="What happened in this visit? What does the patient report? Treatment decisions, observations, next steps…"
                        className="text-sm"
                        data-testid="note-body"
                    />
                    <button
                        type="button"
                        onClick={insertSoapTemplate}
                        className="text-xs text-primary font-semibold hover:underline inline-flex items-center gap-1"
                        data-testid="note-insert-soap"
                    >
                        <Plus size={10} weight="bold" /> Insert SOAP template
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {SOAP_FIELDS.map((f) => (
                        <div key={f.key}>
                            <Label htmlFor={`soap-${f.key}`} className="text-xs uppercase tracking-wider font-bold text-muted-foreground">
                                {f.label}
                                <span className="ml-2 text-[10px] text-muted-foreground/60 normal-case font-normal tracking-normal">{f.hint}</span>
                            </Label>
                            <Textarea
                                id={`soap-${f.key}`}
                                rows={3}
                                value={soap[f.key]}
                                onChange={(e) => setSoap((p) => ({ ...p, [f.key]: e.target.value }))}
                                className="mt-1 text-sm"
                                data-testid={`note-soap-${f.key}`}
                            />
                        </div>
                    ))}
                </div>
            )}

            <div className="flex items-center justify-end gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={onCancel} className="rounded-full" data-testid="note-cancel">
                    <CloseIcon size={12} weight="bold" className="mr-1" /> Cancel
                </Button>
                <Button size="sm" onClick={save} disabled={!hasContent || submitting} className="rounded-full" data-testid="note-save">
                    {submitting ? "Saving…" : (isEdit ? "Save changes" : "Save note")}
                </Button>
            </div>
        </div>
    );
}

function ModePill({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-full transition-colors ${
                active ? "bg-primary text-white" : "text-foreground/70 hover:text-foreground"
            }`}
        >
            {children}
        </button>
    );
}

function NoteRow({ note, caseCode, currentUserId, onEdit, onDeleted, isEditing, hideCaseChip = false }) {
    if (isEditing) return null;
    const canEdit = note.author_id === currentUserId;
    const visitDate = note.visit_at ? new Date(note.visit_at) : new Date(note.created_at);

    const deleteIt = async () => {
        if (!await confirmDialog({ title: "Delete this note? This cannot be undone.", confirmText: "Confirm", tone: "danger" })) return;
        try {
            await api.delete(`/doctor/notes/${note.id}`);
            toast.success("Note deleted");
            onDeleted && onDeleted();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to delete");
        }
    };

    return (
        <div className="px-6 py-5 hover:bg-secondary/20 transition-colors" data-testid={`note-row-${note.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="font-display text-base font-medium text-foreground tabular-nums">
                        {visitDate.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-muted-foreground">{note.author_name || "—"}</span>
                    <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full ${
                        note.note_type === "soap"
                            ? "bg-primary/10 text-primary"
                            : "bg-foreground/5 text-foreground/70"
                    }`}>
                        {note.note_type === "soap" ? "SOAP" : "Note"}
                    </span>
                    {caseCode && !hideCaseChip && (
                        <span className="text-[10px] font-mono text-muted-foreground">· {caseCode}</span>
                    )}
                </div>
                {canEdit && (
                    <div className="flex items-center gap-0.5">
                        <Button variant="ghost" size="icon" onClick={onEdit} className="h-7 w-7 text-muted-foreground hover:text-foreground" data-testid={`note-edit-${note.id}`}>
                            <PencilSimple size={12} weight="duotone" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={deleteIt} className="h-7 w-7 text-muted-foreground hover:text-rose-600" data-testid={`note-delete-${note.id}`}>
                            <TrashSimple size={12} weight="duotone" />
                        </Button>
                    </div>
                )}
            </div>

            {note.note_type === "soap" ? (
                <div className="space-y-2.5 text-sm">
                    {SOAP_FIELDS.map((f) => {
                        const val = note.soap?.[f.key];
                        if (!val || !val.trim()) return null;
                        return (
                            <div key={f.key}>
                                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-0.5">{f.label}</div>
                                <div className="text-sm whitespace-pre-wrap text-foreground/90 leading-relaxed">{val}</div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="text-sm whitespace-pre-wrap text-foreground/90 leading-relaxed">{note.body}</div>
            )}
        </div>
    );
}
